// The deals board: a column per state, a card per deal, and a state change
// made by dragging the card.
//
// The same query as the list — search, filters and sort all at the API — with
// the paging taken away, because a board with a page 2 is a board hiding some
// of its columns' totals. What each column is worth is the chosen MEASURE
// (see `BOARD_MEASURES`) summed over the deals in it, shown as one figure in
// the reader's report currency (see `ColumnTotals`).
//
// Dropping a card is exactly the list's in-place state edit: a one-field PATCH
// with `If-Match`, a 409 reported on the card with the retry offered rather
// than taken. The card is drawn in its new column the moment it is dropped —
// a card that snapped back for the round trip would read as a refused drop —
// and stays there on the strength of the pending move until the server's row
// carries a newer version and says the same thing (see `placedState`).

import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";
import { DealState } from "@crm/shared";
import type { DealListItem } from "@crm/shared";
import styles from "./DealsBoard.module.css";
import toolbarStyles from "./DealsList.module.css";
import { Badge, toneFor } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardBody } from "../../design/Card.js";
import { EmptyState, Notice, ProblemMessage } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { PartnerSwatch } from "../../components/PartnerSwatch.js";
import { useDealMutations } from "../../api/mutations.js";
import { isApiProblem } from "../../api/problem.js";
import { useHasRole } from "../../auth/useAuth.js";
import {
  useAllDeals,
  useFxRates,
  usePartnerColors,
  type DealsQueryState,
} from "../../api/queries.js";
import { formatCalendarDate, formatDateRange, todayIsoDate } from "../../format/calendarDate.js";
import { pickRate } from "../../format/displayCurrency.js";
import { formatCount, formatMoney, formatPercent, formatRate } from "../../format/money.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { dealStateColor, dealStateLabel } from "../../format/labels.js";
import { DealsToolbar, dealsFiltersActive } from "./DealsToolbar.js";
import {
  BOARD_MEASURES,
  boardMeasureLabel,
  columnTotals,
  convertColumnTotals,
  figureOf,
  moveIsRetired,
  placedState,
  type BoardMeasure,
  type ColumnTotal,
  type PendingMove,
} from "./boardModel.js";
import type { DealsViewMode } from "./dealsSearch.js";

const DRAG_MIME = "text/plain";

export interface DealsBoardProps {
  state: DealsQueryState;
  onStateChange: (next: DealsQueryState) => void;
  view: DealsViewMode;
  onViewChange: (next: DealsViewMode) => void;
  /** Which figure the columns add up. */
  measure: BoardMeasure;
  onMeasureChange: (next: BoardMeasure) => void;
  /** Absent for a reader — the create action is the page's to own and to gate. */
  onCreate?: () => void;
}

export function DealsBoard({
  state,
  onStateChange,
  view,
  onViewChange,
  measure,
  onMeasureChange,
  onCreate,
}: DealsBoardProps): ReactNode {
  const query = useAllDeals(state);
  const mutations = useDealMutations();
  const canEdit = useHasRole("crm_editor");
  const partnerColors = usePartnerColors();
  const [moves, setMoves] = useState<ReadonlyMap<string, PendingMove>>(new Map());
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<DealState | null>(null);
  // The id being dragged, readable from a drop handler. `dataTransfer` is the
  // browser's channel for the same fact, but it is empty during `dragover` in
  // some browsers and absent altogether under test — so this is the record,
  // and the transfer is set for the browser's sake.
  const draggingRef = useRef<string | null>(null);

  const setMove = useCallback((id: string, move: PendingMove | null): void => {
    setMoves((previous) => {
      const next = new Map(previous);
      if (move === null) next.delete(id);
      else next.set(id, move);
      return next;
    });
  }, []);

  const move = useCallback(
    async (deal: DealListItem, to: DealState, version: number): Promise<void> => {
      setMove(deal.id, { to, version, status: "saving" });
      try {
        await mutations.update.mutateAsync({ id: deal.id, body: { state: to }, version });
        setMove(deal.id, { to, version, status: "done" });
      } catch (error) {
        // A 409 is not a failure to report and forget: the move was refused
        // because this card is older than the stored deal, and re-applying it
        // is a decision only the person who made it can take.
        if (isApiProblem(error) && error.code === "crm-conflict") {
          setMove(deal.id, { to, version, status: "stale" });
        } else {
          setMove(deal.id, { to, version, status: "failed", error });
        }
      }
    },
    [mutations.update, setMove],
  );

  const moveToCurrent = useCallback(
    (deal: DealListItem, to: DealState): void => {
      void (async () => {
        try {
          const current = await mutations.fetchCurrent(deal.id);
          await move(deal, to, current.version);
        } catch (error) {
          setMove(deal.id, { to, version: deal.version, status: "failed", error });
        }
      })();
    },
    [move, mutations, setMove],
  );

  // Once the server's row has caught up with a finished move, the card's
  // column no longer depends on remembering it.
  const items = query.data?.items;
  useEffect(() => {
    if (items === undefined) return;
    setMoves((previous) => {
      let next: Map<string, PendingMove> | null = null;
      for (const [id, pending] of previous) {
        if (
          !moveIsRetired(
            items.find((deal) => deal.id === id),
            pending,
          )
        ) {
          continue;
        }
        next ??= new Map(previous);
        next.delete(id);
      }
      return next ?? previous;
    });
  }, [items]);

  const endDrag = (): void => {
    draggingRef.current = null;
    setDragging(null);
    setOver(null);
  };

  const dropOn = (event: DragEvent<HTMLElement>, to: DealState): void => {
    event.preventDefault();
    const id = draggingRef.current ?? readDragged(event);
    endDrag();
    if (!canEdit || id === null) return;
    const deal = items?.find((candidate) => candidate.id === id);
    if (deal === undefined || placedState(deal, moves.get(deal.id)) === to) return;
    void move(deal, to, deal.version);
  };

  // A state filter narrows the board to those columns: a card dragged into a
  // column the filter hides would vanish on drop, which is the one thing a
  // drop must never look like.
  const columns: readonly DealState[] =
    state.states.length === 0 ? DealState.options : state.states;
  const filtersActive = dealsFiltersActive(state);

  return (
    <div className={styles.root}>
      <DealsToolbar
        state={state}
        onStateChange={onStateChange}
        view={view}
        onViewChange={onViewChange}
        total={query.data?.total}
        {...(onCreate === undefined ? {} : { onCreate })}
        extra={
          // The board's own control: which figure a column adds up. It changes
          // every total on screen, so it sits with the filters that do the same.
          <select
            className={toolbarStyles.control}
            aria-label="Column value"
            value={measure}
            onChange={(event) => {
              const picked = BOARD_MEASURES.find((option) => option === event.target.value);
              if (picked !== undefined) onMeasureChange(picked);
            }}
          >
            {BOARD_MEASURES.map((option) => (
              <option key={option} value={option}>
                {boardMeasureLabel(option)}
              </option>
            ))}
          </select>
        }
      />

      <QueryBoundary query={query} label="the deals board">
        {(page) => {
          if (page.items.length === 0) {
            return (
              <Card>
                <CardBody>
                  <EmptyState
                    title="No deals match these filters"
                    description={
                      filtersActive
                        ? "Clear the search or the filters to see the rest of the pipeline."
                        : "Create a deal to start the pipeline."
                    }
                  />
                </CardBody>
              </Card>
            );
          }

          const byState = new Map<DealState, DealListItem[]>();
          for (const deal of page.items) {
            const placed = placedState(deal, moves.get(deal.id));
            byState.set(placed, [...(byState.get(placed) ?? []), deal]);
          }

          return (
            <>
              <div className={styles.board}>
                {columns.map((column) => (
                  <BoardColumn
                    key={column}
                    state={column}
                    deals={byState.get(column) ?? []}
                    measure={measure}
                    partnerColors={partnerColors}
                    canEdit={canEdit}
                    dragging={dragging}
                    over={over === column}
                    moves={moves}
                    onDragStart={(deal, event) => {
                      draggingRef.current = deal.id;
                      setDragging(deal.id);
                      writeDragged(event, deal.id);
                    }}
                    onDragEnd={endDrag}
                    onDragOver={(event) => {
                      if (!canEdit || draggingRef.current === null) return;
                      // Without this the browser refuses the drop.
                      event.preventDefault();
                      if (over !== column) setOver(column);
                    }}
                    onDragLeave={(event) => {
                      const next = event.relatedTarget;
                      if (next instanceof Node && event.currentTarget.contains(next)) return;
                      if (over === column) setOver(null);
                    }}
                    onDrop={(event) => dropOn(event, column)}
                    onMoveToCurrent={moveToCurrent}
                    onDismiss={(deal) => setMove(deal.id, null)}
                  />
                ))}
              </div>

              {canEdit && (
                <p className={styles.hint}>
                  {`Drag a deal into another column to change its state. Each column's figure is its deals' ${boardMeasureLabel(measure).toLowerCase()} in your report currency, with "≈" where deals in the other currency were converted at today's rate. Every figure is our share, after the partner's. "Est." marks an estimate — implementation plus 12 months of recurring from the deal's estimate fields — kept apart from contracted money.`}
                </p>
              )}
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}

function writeDragged(event: DragEvent<HTMLElement>, id: string): void {
  // Optional-chained because jsdom has no DataTransfer; the ref above is the
  // record this screen actually reads.
  const transfer: DataTransfer | undefined = event.dataTransfer;
  transfer?.setData(DRAG_MIME, id);
  if (transfer !== undefined) transfer.effectAllowed = "move";
}

function readDragged(event: DragEvent<HTMLElement>): string | null {
  const transfer: DataTransfer | undefined = event.dataTransfer;
  const id = transfer?.getData(DRAG_MIME) ?? "";
  return id === "" ? null : id;
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

interface BoardColumnProps {
  state: DealState;
  deals: readonly DealListItem[];
  measure: BoardMeasure;
  partnerColors: ReadonlyMap<string, string>;
  canEdit: boolean;
  dragging: string | null;
  over: boolean;
  moves: ReadonlyMap<string, PendingMove>;
  onDragStart: (deal: DealListItem, event: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragLeave: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
  onMoveToCurrent: (deal: DealListItem, to: DealState) => void;
  onDismiss: (deal: DealListItem) => void;
}

function BoardColumn({
  state,
  deals,
  measure,
  partnerColors,
  canEdit,
  dragging,
  over,
  moves,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onMoveToCurrent,
  onDismiss,
}: BoardColumnProps): ReactNode {
  const label = dealStateLabel(state);
  return (
    <section
      className={`${styles.column} ${over ? styles.over : ""}`}
      role="group"
      aria-label={label}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <header className={styles.columnHead}>
        <div className={styles.columnTitle}>
          <Badge shape="pill" tone={toneFor(dealStateColor(state))}>
            {label}
          </Badge>
          <span className={styles.columnCount}>
            {`${formatCount(deals.length)} ${deals.length === 1 ? "deal" : "deals"}`}
          </span>
        </div>
        <ColumnTotals state={state} deals={deals} measure={measure} />
      </header>

      <ul className={styles.cards}>
        {deals.map((deal) => (
          <DealCard
            key={deal.id}
            deal={deal}
            measure={measure}
            partnerColor={partnerColors.get(deal.partner_id) ?? null}
            canEdit={canEdit}
            dragging={dragging === deal.id}
            move={moves.get(deal.id)}
            onDragStart={(event) => onDragStart(deal, event)}
            onDragEnd={onDragEnd}
            onMoveToCurrent={(to) => onMoveToCurrent(deal, to)}
            onDismiss={() => onDismiss(deal)}
          />
        ))}
      </ul>
    </section>
  );
}

/**
 * What the column is worth under the chosen measure: ONE figure, in the
 * reader's report currency, because the header adds deals up and every screen
 * that adds deals up reads in that currency. A column with a deal in the other
 * currency is converted at today's rate and marked "≈"; the cards below keep
 * each deal's own currency. With no rate on file it falls back to one line per
 * currency, never a figure that counts USD as CAD.
 *
 * An empty column says it is empty rather than showing a zero: a "$0.00" over
 * no deals would be a figure about nothing.
 */
function ColumnTotals({
  state,
  deals,
  measure,
}: {
  state: DealState;
  deals: readonly DealListItem[];
  measure: BoardMeasure;
}): ReactNode {
  const target = useReportCurrency();
  const rates = useFxRates();
  const today = todayIsoDate();
  const rate = pickRate(rates.data?.items ?? [], today);
  const totals = columnTotals(deals, measure);
  if (totals.length === 0) return <div className={styles.totalsEmpty}>No deals</div>;

  const one = convertColumnTotals(totals, target, rate?.usd_to_cad ?? null);
  const lines: readonly (ColumnTotal & { converted?: boolean })[] = one === null ? totals : [one];

  return (
    <div className={styles.totals} aria-label={`${dealStateLabel(state)} total`}>
      {lines.map((total) => (
        <TotalLine
          key={total.currency}
          total={total}
          approximate={total.converted === true}
          title={
            total.converted === true && rate !== null
              ? `CAD and USD deals added up in ${target} at USD→CAD ${formatRate(rate.usd_to_cad)}, the rate in force since ${formatCalendarDate(rate.effective_date)}. Each card shows its deal's own currency.`
              : undefined
          }
        />
      ))}
    </div>
  );
}

function TotalLine({
  total,
  approximate,
  title,
}: {
  total: ColumnTotal;
  approximate: boolean;
  title: string | undefined;
}): ReactNode {
  const mark = approximate ? "≈ " : "";
  return (
    <div className={styles.totalLine} {...(title === undefined ? {} : { title })}>
      {(total.minor !== 0 || total.estimateCount === 0) && (
        <span className={`${styles.total} mono`}>
          {`${mark}${formatMoney(total.minor, total.currency)}`}
        </span>
      )}
      {/* Estimates are summed apart and drawn as estimates: a column of
          prospects is worth a guess, and one figure that mixed the two would
          read as contracted. */}
      {total.estimateCount > 0 && (
        <span
          className={`${styles.estimateTotal} mono`}
          title={`${formatCount(total.estimateCount)} estimated, from the deals' estimate fields — not contracted`}
        >
          {`${total.minor !== 0 ? "+ " : mark}${formatMoney(total.estimateMinor, total.currency)} est.`}
        </span>
      )}
      {/* In neither figure, and said so: a metered deal's value is not a
          number until the facts arrive, and a sum that left it out silently
          would read as the whole column. */}
      {total.unknownCount > 0 && (
        <span className={styles.unknown}>
          {`${formatCount(total.unknownCount)} without a figure`}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface DealCardProps {
  deal: DealListItem;
  measure: BoardMeasure;
  /** The partner's colour, drawn down the card's left edge. */
  partnerColor: string | null;
  canEdit: boolean;
  dragging: boolean;
  move: PendingMove | undefined;
  onDragStart: (event: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onMoveToCurrent: (to: DealState) => void;
  onDismiss: () => void;
}

function DealCard({
  deal,
  measure,
  partnerColor,
  canEdit,
  dragging,
  move,
  onDragStart,
  onDragEnd,
  onMoveToCurrent,
  onDismiss,
}: DealCardProps): ReactNode {
  const saving = move?.status === "saving";
  const classes = [styles.card, dragging ? styles.dragging : "", saving ? styles.saving : ""].join(
    " ",
  );
  const figure = figureOf(deal, measure);

  return (
    <li
      className={classes}
      // The colour runs down the edge: the one place on a card that reads at a
      // glance across a whole column, without shouting over the name.
      style={partnerColor === null ? undefined : { borderLeftColor: partnerColor }}
      draggable={canEdit && !saving}
      aria-busy={saving}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <Link to="/deals/$dealId" params={{ dealId: deal.id }} className={styles.customer}>
        {deal.customer_name}
      </Link>
      <span className={styles.partner}>
        <PartnerSwatch color={partnerColor} />
        {deal.partner_name}
      </span>
      <div className={styles.figures}>
        {figure === null ? (
          <span
            className={styles.noFigure}
            title="A metered clause makes this deal's value depend on the facts"
          >
            No figure yet
          </span>
        ) : figure.kind === "estimate" ? (
          <span
            className={styles.estimateFigure}
            title={`An estimate, our share: implementation plus 12 months of recurring from the deal's estimate fields, at ${deal.estimated_share_source === "assumed" ? "the ASSUMED share (no agreement states one)" : deal.estimated_share_source === "deal" ? "the deal's own share" : "the agreement's share"}. Not a contract.`}
          >
            <span className="mono">{formatMoney(figure.minor, deal.currency)}</span>
            <Badge shape="tag" tone="info">
              Est.
            </Badge>
          </span>
        ) : (
          <span className="mono">{formatMoney(figure.minor, deal.currency)}</span>
        )}
        <span className={styles.winProb}>{`${formatPercent(deal.win_prob)} win`}</span>
      </div>
      <span className={`${styles.start} mono`}>
        {deal.start_date_is_firm
          ? formatCalendarDate(deal.start_date_earliest)
          : formatDateRange(deal.start_date_earliest, deal.start_date_latest)}
      </span>

      {move?.status === "stale" && (
        <div className={styles.cardMessage}>
          <Notice
            tone="warn"
            title="Someone else changed this deal."
            actions={
              <>
                <Button small onClick={() => onMoveToCurrent(move.to)}>
                  Move it anyway
                </Button>
                <Button small variant="subtle" onClick={onDismiss}>
                  Leave it
                </Button>
              </>
            }
          >
            {`This card is from before their edit, so it was not moved to ${dealStateLabel(move.to)}. Reload the board to see what they changed, or move it to the version that exists now.`}
          </Notice>
        </div>
      )}

      {move?.status === "failed" && (
        <div className={styles.cardMessage}>
          <ProblemMessage
            error={move.error}
            action={`move this deal to ${dealStateLabel(move.to)}`}
            onRetry={onDismiss}
          />
        </div>
      )}
    </li>
  );
}
